import { LEARN_HEADS, type LearnHeadName } from "../components/common/learn-heads.generated";
import { PUBLIC_ART_BANDS } from "../components/public-art/public-art-bands";
import { learnArtBandSrc } from "../components/learn/LearnArtBand";
import { PRICING_ART_BANDS } from "../components/pricing/PricingArtBand";
import type { GlossaryTopicSlug } from "./glossary-topics";

/*
公開の頁の頭の帯 (PublicPageBand) に敷く絵を、住所の区画から決める 1 か所。/luagate の lib/luagate/page-band.ts にあたる。
絵は既存の絵 (学ぶ頁の帯 learn-heads、頁ごとの頭の帯 public-art-bands、立体の帯 pub3d-e-art、学ぶ頁の帯 LearnArtBand、
料金の帯 PricingArtBand) だけを使い、新しく描かない。
区画は住所の頭で引き、いちばん長く一致した区画が勝つ。どこにも当たらない頁は既定 (home) の帯になるので、帯の無い頁は出ない。
頁ごとにもっと合う絵があるとき (コースの 1 本がそのトラックの帯を使う、など) は、ここに足すか PublicPageBand の art で名前を渡す
*/
/**
 * 研究所の節 (受講生の声、講師、代表) の一覧は、今まで頭に置いてきた節の絵 (public/assets/page-art、PublicArtBand) を帯に使う
 */
const LAB_SECTION_ARTS = ["voices", "teachers", "founder"] as const;
type LabSectionArt = (typeof LAB_SECTION_ARTS)[number];

export type PublicBandArt = LearnHeadName | LabSectionArt | { readonly src: string };

/** 区画の頭 → 帯の絵の名前 (LEARN_HEADS のキー)、または絵の住所を直に渡す形 */
const BY_SECTION: Readonly<Record<string, PublicBandArt>> = {
  "/learn/courses": "courses",
  "/learn/lessons": "lessons",
  "/learn/mistakes": "lessons",
  "/learn": "courses",
  "/interview-questions": "interview",
  // AIコーディングの区画。claude-code は同じ名前の絵がある。codex と mcp はぴったりの絵が無いので近い絵で代える
  "/ai-coding/claude-code": "claude-code",
  "/ai-coding/codex": "ai-use",
  "/ai-coding/mcp": "ai-app",
  "/ai-coding": "vibe-coding",
  // 稼ぐの柱。作って納める仕事の頁なので、AI でアプリを作る帯の絵
  "/side-income": "ai-app",
  "/git": "cs",
  "/visualize": "cs",
  // ゆめさく CS (コンピュータサイエンスの学びの場) の入口と道の頁
  "/computer-science": "cs",
  // 高校の「情報 I」の束。プログラムの表記と Python の頁なので、学ぶ頁の Python の帯の絵
  "/joho1": "python",
  // 料金 (今まで頭に敷いていた絵をそのまま帯の絵にする)
  "/code/pricing": { src: PRICING_ART_BANDS.head },
  // 職種別ガイド (学ぶ頁の帯と同じ絵柄)
  "/career": { src: learnArtBandSrc("career") },
  // 未経験からの転職ロードマップ (今まで本文の頭に敷いていた場面の絵をそのまま帯の絵にする)。
  // SVG のままだと広い画面で左の端を伸ばして埋められない (背景の SVG は縦横の比を保つ) ので、同じ絵を webp にした物
  "/career-change": { src: "/assets/page-art/career-change-band-1120-6acec70b25.webp" },
  "/career-change/portfolio": { src: PUBLIC_ART_BANDS.portfolio.wide[1] },
  // 求人 (詳細の頭にあった表とふくろうの絵を、一覧も含めた区画全体の帯にする)
  "/jobs": { src: PUBLIC_ART_BANDS["jobs-detail"].wide[1] },
  "/companies": { src: PUBLIC_ART_BANDS.companies.wide[1] },
  "/ranking": { src: PUBLIC_ART_BANDS["guides-ranking"].wide[1] },
  "/creators": { src: PUBLIC_ART_BANDS.creators.wide[1] },
  "/skills": { src: PUBLIC_ART_BANDS.skills.wide[1] },
  // 道具の帯の SVG (pub3d-e の tools-band-wide) を webp にした物。理由は /career-change と同じ。
  // 左の端のプリンターと芽は、伸ばすと地面に緑の筋が出るので切り落とした (左から 584px)
  "/tools": { src: "/assets/page-art/tools-band-1816-2793a443f1.webp" },
  // パスワード生成の頁。安全の話なので、学ぶ頁のセキュリティの帯の絵を使う (長く一致する区画が勝つので /tools の帯より先に当たる)
  "/tools/password-generator": "security",
  // JSON 整形の頁。専用の絵は無いので、同じ区画 (/git, /glossary) で使っている cs の絵で代える
  "/tools/json-formatter": "cs",
  // 16進数変換・2進数変換の頁。基数変換は IT の基礎知識の話題なので、学ぶ頁の IT パスポートの帯の絵を使う
  "/tools/hex-converter": "it-passport",
  "/tools/binary-converter": "it-passport",
  "/news": { src: PUBLIC_ART_BANDS.news.wide[1] },
  "/about": { src: PUBLIC_ART_BANDS.about.wide[1] },
  "/contact": { src: PUBLIC_ART_BANDS.contact.wide[1] },
  "/privacy-policy": { src: PUBLIC_ART_BANDS.privacy.wide[1] },
  "/terms-of-service": { src: PUBLIC_ART_BANDS.terms.wide[1] },
  "/sitemap": { src: PUBLIC_ART_BANDS.sitemap.wide[1] },
  // 研究所 (/lab) の記事一覧、タグ、記事。節の一覧だけは下の ONLY_PAGE で節の絵
  "/lab": "lessons",
  // 用語辞典。話題の頁と、話題を持つ用語は GLOSSARY_TOPIC_BAND で話題の絵にする
  "/glossary": "cs",
};

/** 用語辞典の話題 → 帯の絵。話題ごとに近い学ぶ頁の帯を借りる (新しく描かない) */
export const GLOSSARY_TOPIC_BAND: Readonly<Record<GlossaryTopicSlug, LearnHeadName>> = {
  ai: "ai-use",
  programming: "python",
  web: "javascript",
  frontend: "html-css",
  backend: "nodejs",
  database: "sql",
  infrastructure: "aws",
  security: "security",
  "development-tools": "claude-code",
  "it-basics": "it-passport",
};

/**
 * その住所の頁だけに使う絵。研究所の節の絵はキャラが帯の 4 割の所に立つので、広い画面で長い題 (節の記事の名前) が重なる。
 * 題の短い節の一覧にだけ使い、節の記事は研究所の帯 (lessons) にする
 */
const ONLY_PAGE: Readonly<Record<string, PublicBandArt>> = {
  "/lab/voices": "voices",
  "/lab/teachers": "teachers",
  "/lab/founder": "founder",
};

/** どの区画にも当たらない頁の帯 */
export const PUBLIC_BAND_DEFAULT: PublicBandArt = "home";

/** 住所から帯の絵の名前。区画の頭は「/」の切れ目で一致させる (/learning が /learn に当たらないように) */
export function publicBandArtFor(pathname: string): PublicBandArt {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (ONLY_PAGE[path]) return ONLY_PAGE[path];
  let best: string | null = null;
  for (const prefix of Object.keys(BY_SECTION)) {
    if (path !== prefix && !path.startsWith(`${prefix}/`)) continue;
    if (best === null || prefix.length > best.length) best = prefix;
  }
  return best === null ? PUBLIC_BAND_DEFAULT : BY_SECTION[best];
}

/**
 * 帯の形 (横 8 対 1、learn-heads) の絵か。ほかの絵 (料金、頁ごとの頭の絵、研究所の節の絵) は横 3.6〜6 対 1 で、
 * 広い画面の帯 (168px) に横幅いっぱいで敷くと上下が大きく切れる (10/25 /code/pricing の階段とかめ)。
 * その絵は PublicPageBand が .pub-band--fit-right にし、高さで合わせて右下に置く
 */
export function isWideBandArt(art: PublicBandArt): boolean {
  return typeof art === "string" && art in LEARN_HEADS;
}

/** 帯の絵の住所 (広い画面の版。スマホでも同じ 1 枚を左の端で切り抜く。切り方は components/page-band/page-band.css) */
export function publicBandSrc(art: PublicBandArt): string {
  if (typeof art !== "string") return art.src;
  if (art in LEARN_HEADS) return LEARN_HEADS[art as LearnHeadName].wide;
  // 節の絵は 2240 幅の版 (learn-heads の 2880 幅に近い大きさ)
  return PUBLIC_ART_BANDS[art as LabSectionArt].wide[3];
}
